/**
 * Wave 8 — Voice navigation.
 *
 * Turns a spoken phrase ("open chat", "take me to the dashboard") into an
 * in-app route. Pure matching logic so it can be unit tested and reused by
 * both the global listener and the voice conversation pipeline.
 */

export interface VoiceRoute {
  route: string;
  label: string;
  phrases: string[];
}

export const VOICE_ROUTES: VoiceRoute[] = [
  { route: "/chat", label: "GenieBot Chat", phrases: ["chat", "geniebot chat", "talk to geniebot", "conversation"] },
  { route: "/dashboard", label: "Dashboard", phrases: ["dashboard", "home", "my dashboard"] },
  { route: "/tools", label: "Tools", phrases: ["tools", "tool list", "geniebot tools"] },
  { route: "/tool-finder", label: "Tool Finder", phrases: ["tool finder", "find a tool", "which tool"] },
  { route: "/batch-runs", label: "Batch Runs", phrases: ["batch runs", "batch", "bulk runs"] },
  { route: "/analytics", label: "Analytics", phrases: ["analytics", "my stats", "statistics"] },
  { route: "/achievements", label: "Achievements", phrases: ["achievements", "badges", "trophies"] },
  { route: "/games", label: "Games Arcade", phrases: ["games", "arcade", "play a game"] },
  { route: "/theme-lab", label: "Theme Lab", phrases: ["theme lab", "themes", "change theme"] },
  { route: "/gallery", label: "Community Gallery", phrases: ["gallery", "community gallery", "public gallery"] },
  { route: "/settings/data-controls", label: "Data Controls", phrases: ["data controls", "privacy", "my data"] },
  { route: "/settings/api-keys", label: "API Keys", phrases: ["api keys", "developer keys", "my api key"] },
  { route: "/profile-settings", label: "Profile Settings", phrases: ["profile", "profile settings", "account settings"] },
  { route: "/affiliate", label: "Affiliate Program", phrases: ["affiliate", "referrals", "referral program"] },
  { route: "/pic-editor", label: "Pic Editor", phrases: ["pic editor", "image editor", "edit a photo"] },
  { route: "/videos", label: "Video Library", phrases: ["videos", "video library", "my videos"] },
];

const NOISE = /^(please\s+|hey\s+genie(bot)?[,\s]+|genie(bot)?[,\s]+|can you\s+|could you\s+)/gi;
const VERBS = /^(go to|goto|open|navigate to|take me to|show me|show|launch|jump to|visit)\s+/i;
const ARTICLES = /^(the|my|a)\s+/i;

export function normalizeVoicePhrase(input: string): string {
  let text = (input || "").toLowerCase().trim().replace(/[.!?,]+$/g, "");
  text = text.replace(NOISE, "").trim();
  const hadVerb = VERBS.test(text);
  text = text.replace(VERBS, "").trim();
  text = text.replace(ARTICLES, "").trim();
  text = text.replace(/\s+(page|screen|section)$/i, "").trim();
  return hadVerb ? text : text;
}

export interface VoiceNavMatch {
  route: string;
  label: string;
  confidence: number;
}

/**
 * Match a spoken phrase to a route. Requires a navigation verb OR an exact
 * phrase hit so ordinary conversation isn't hijacked into navigation.
 */
export function matchVoiceRoute(spoken: string): VoiceNavMatch | null {
  const raw = (spoken || "").toLowerCase().trim();
  if (!raw) return null;
  const hasVerb = VERBS.test(raw.replace(NOISE, "").trim());
  const phrase = normalizeVoicePhrase(raw);
  if (!phrase) return null;

  let best: VoiceNavMatch | null = null;
  for (const entry of VOICE_ROUTES) {
    for (const candidate of entry.phrases) {
      let confidence = 0;
      if (phrase === candidate) confidence = 1;
      else if (hasVerb && phrase.startsWith(candidate)) confidence = 0.85;
      else if (hasVerb && phrase.includes(candidate)) confidence = 0.7;
      if (confidence > (best?.confidence ?? 0)) {
        best = { route: entry.route, label: entry.label, confidence };
      }
    }
  }

  if (!best) return null;
  if (best.confidence < 1 && !hasVerb) return null;
  return best;
}
